// phase-compiler-six-tabs — small presentational helpers for the six-tab
// compiler workspace that CompilerPage composes:
//
//   • SelectedFileHeader — the ONE shared selected-file header rendered once by
//     CompilerWorkspace (between the tab strip and the active panel) for every
//     document-oriented tab: filename + lifecycle badge + signature badge +
//     auto-rename affordance + cover-letter sync banner. It also carries a
//     compact document PICKER so the selected document stays reachable when the
//     CTD tree is hidden or the screen is narrow (prompt §20). Selecting here is
//     a plain selection — it never changes the active main tab.
//
//   • WorkspaceEmptyState — the honest "nothing selected / nothing here" state a
//     document-scoped section shows instead of a reassuring blank.
//
// These are deliberately thin: CompilerPage owns selection, permissions and the
// write callbacks; this file only arranges existing canonical affordances.

import { type CSSProperties, type ReactElement, type ReactNode } from 'react';
import type { Document } from '@/api/v2Client';
import { DocumentBadge } from './DocumentBadge';
import { AutoRenameAffordance } from '@/components/documents/AutoRenameAffordance';
import { CoverLetterSyncBanner } from '@/components/documents/CoverLetterSyncBanner';
import { SignatureBadge } from '@/components/documents/SignatureBadge';

export interface SelectedFileHeaderProps {
  readonly documents: readonly Document[];
  readonly selectedDocument: Document | null;
  readonly canEdit: boolean;
  readonly onSelectDocument: (documentId: string) => void;
  readonly onRenamed: (newName: string) => void;
  readonly onError: (message: string) => void;
  readonly onSnapshotCaptured: () => void;
}

const headerWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  marginBottom: 4,
  flex: '0 0 auto',
};

const fileNameStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--text)',
  wordBreak: 'break-all',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
};

const pickerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  color: 'var(--text-muted)',
};

const pickerStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 12,
  padding: '3px 6px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--text)',
};

/**
 * The shared selected-file header. Rendered once for every document-oriented
 * tab so the operator never loses track of which document the document-scoped
 * controls act on. Renders nothing at all when the submission has no documents
 * (there is nothing to select or show).
 */
export function SelectedFileHeader({
  documents,
  selectedDocument,
  canEdit,
  onSelectDocument,
  onRenamed,
  onError,
  onSnapshotCaptured,
}: SelectedFileHeaderProps): ReactElement | null {
  if (documents.length === 0) return null;

  return (
    <div data-testid="workspace-selected-file-header" style={headerWrap}>
      {/* Document picker — keeps selection reachable with the tree hidden or on a
          narrow screen. A plain selection; it does NOT change the active tab. */}
      <div style={pickerRow}>
        <label htmlFor="workspace-document-picker" style={{ whiteSpace: 'nowrap' }}>
          Document
        </label>
        <select
          id="workspace-document-picker"
          data-testid="workspace-document-picker"
          style={pickerStyle}
          value={selectedDocument?.Id ?? ''}
          onChange={(e) => {
            if (e.target.value) onSelectDocument(e.target.value);
          }}
        >
          <option value="" disabled>
            Select a document…
          </option>
          {documents.map((d) => (
            <option key={d.Id} value={d.Id}>
              {d.FileName}
            </option>
          ))}
        </select>
      </div>

      {selectedDocument ? (
        <>
          <p data-testid="document-inspector-filename" style={fileNameStyle}>
            <span>{selectedDocument.FileName}</span>
            <DocumentBadge
              operation={selectedDocument.LifecycleOperation as string | null | undefined}
              size="md"
            />
            <SignatureBadge
              status={
                selectedDocument.SignatureStatus as 'digital' | 'wet' | 'none' | null | undefined
              }
            />
            <AutoRenameAffordance
              documentId={selectedDocument.Id}
              currentFileName={selectedDocument.FileName}
              rowVersion={selectedDocument.RowVersion}
              canEdit={canEdit}
              onRenamed={onRenamed}
              onError={onError}
            />
          </p>
          <CoverLetterSyncBanner
            documentId={selectedDocument.Id}
            ctdSectionCode={(selectedDocument.CtdSectionCode as string | null | undefined) ?? null}
            submissionId={selectedDocument.SubmissionId}
            canEdit={canEdit}
            onSnapshotCaptured={onSnapshotCaptured}
            onError={onError}
          />
        </>
      ) : (
        <p data-testid="workspace-no-selection-hint" style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
          No document selected — pick one above, or from the CTD tree.
        </p>
      )}
    </div>
  );
}

const emptyStyle: CSSProperties = {
  padding: '20px 12px',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  textAlign: 'center',
  fontSize: 13,
};

/** Honest empty state for a document-scoped section with no selection. */
export function WorkspaceEmptyState({
  testId,
  children,
}: {
  testId: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div data-testid={testId} style={emptyStyle}>
      {children}
    </div>
  );
}
